// StoreInfoInput.js
import React, { useEffect, useState } from 'react';
import { Button, DialogContent, DialogActions, CircularProgress, Grid, Popover } from '@mui/material';
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmTextField from '../../common/VzmTextField';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox, VzmPrint } from '../../common/VzmStyled';
import DaumAddressSearch from '../../common/DaumAddressSearch';
import VzmCheckbox from '../../common/VzmCheckbox';
import PhoneField from '../../common/PhoneField';
import { useReactiveVar } from '@apollo/client';
import StoreIcon from '@mui/icons-material/Store';
import InputUtil from '../../value/InputUtil';
import { StoreInfoDictionary } from './StoreInfoDict';
import { storeTypeListRepo } from '../../value/AppModel';
import IsValid from '../../value/IsValid';

const InputMap = StoreInfoDictionary.inputMap;
const defaultInputData = InputUtil.defaultValuesFromInputMap(InputMap);

/**
 * (STORE_INFO) 정보 생성 또는 편집 Dialog
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-09-02 17:40:08 using DbdbTap
 */
export default function StoreInfoInput({
    open,
    item,
    defaultStoreType, // if item is null, default storeTypeCd
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const storeTypeListData = useReactiveVar(storeTypeListRepo);
    const [prefix, setPrefix] = useState('');
    const [postfix, setPostfix] = useState('');
    const [rootName, setRootName] = useState('');
    const [anchorAddr, setAnchorAddr] = useState(null);
    // const codeTree = useReactiveVar(codeTreeRepo);
    const openConfirmDialog = useConfirmDialog();

    useEffect(()=>{
        if(open) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            if(item) {
                inputSet.data = {...item};
                inputSet.data.ignoreDup = false;
                editing = true;
            }
            else {
                inputSet.data = {...defaultInputData};
                if(defaultStoreType) {
                    inputSet.data.storeTypeCd = defaultStoreType.storeTypeCd;
                }
                else inputSet.data.storeTypeCd = null;
            }
            setInput(inputSet);
            setIsEdit(editing);
        }
    }, [open, item, defaultStoreType]);

    useEffect(()=>{
        if(open && storeTypeListData.records.length > 0) {
            // find storeTypeCd from storeTypeListData.records (input.data.storeTypeCd)
            if(input.data.storeTypeCd) {
                const rec = storeTypeListData.records.find((r)=>r.storeTypeCd === input.data.storeTypeCd);
                const prefix = rec.storePrefix || '';
                const postfix = rec.storePostfix || '';
                setPrefix(prefix);
                setPostfix(postfix);

                if(isEdit) {
                    // parse storeName to prefix and postfix and rootName
                    let name = input.data.storeName;
                    if(prefix.length > 0 && name.startsWith(prefix)) {
                        name = name.substring(prefix.length);
                    }
                    if(postfix.length > 0 && name.endsWith(postfix)) {
                        name = name.substring(0, name.length - postfix.length);
                    }
                    setRootName(name);
                }
            }
            else {
                setPrefix('');
                setPostfix('');
                setRootName('');
            }
        }
    }, [open, input.data, isEdit, storeTypeListData.records]);

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onChangeRootName = (id, value, error) => {
        setRootName(value);
        onChangeValue(id, prefix + value + postfix, error);
    };

    const onSelectAddress = (addr) => {
        if(addr) {
            const newInputOne = InputUtil.reflectData(input, InputMap.zipCd.id, addr.zipCd, !IsValid.zipCode(addr.zipCd), InputMap, isEdit);
            const newInput = InputUtil.reflectData(newInputOne, InputMap.addr.id, addr.addr, !IsValid.string(addr.addr, 0, 100), InputMap, isEdit);
            setInput(newInput);
        }
        setAnchorAddr(null);
    };

    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            startIcon={<StoreIcon size={AppWord.LARGE} />}
            title={isEdit ? '점포 정보 수정' : '점포 정보 등록'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={4}>
                        <VzmSelector
                            dict={InputMap.storeTypeCd}
                            value={input.data.storeTypeCd}
                            onChange={onChangeValue}
                            fullWidth={true}
                            list={storeTypeListData.records}
                        />
                    </Grid>
                    <Grid item xs={8}></Grid>

                    <Grid item xs={2}>
                        <VzmTextField
                            dict={{id:'storePrefix', label:'접두코드'}}
                            value={prefix || '[없음]'}
                            disabled={true}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmTextField
                            dict={InputMap.storeName}
                            value={rootName || ''}
                            onChange={onChangeRootName}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <VzmTextField
                            dict={{id:'storePostfix', label:'접미코드'}}
                            value={postfix || '[없음]'}
                            disabled={true}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}></Grid>

                    <Grid item xs={2}>
                        <VzmPrint label={InputMap.storeId.label}
                            labelColor={'#999'} color='#777'
                            value={isEdit ? input.data.storeId : '[자동생성]'} />
                    </Grid>
                    <Grid item xs={6}>
                        <VzmPrint label="최종 점포명"
                            labelColor={'#999'} color='#777'
                            value={input.data.storeName} />
                    </Grid>
                    <Grid item xs={4}></Grid>

                    
                    <Grid item xs={2}>
                        <VzmTextField
                            dict={InputMap.zipCd}
                            value={input.data.zipCd}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={8}>
                        <VzmTextField
                            dict={InputMap.addr}
                            value={input.data.addr}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={2}>

                            <Button
                                style={{marginLeft:8}} size={AppWord.LARGE}
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.primary}
                                onClick={(e)=>{setAnchorAddr(e.currentTarget);}}
                            >
                                주소찾기
                            </Button>
                            <Popover
                                anchorEl={anchorAddr}
                                open={Boolean(anchorAddr)}
                                onClose={()=>{setAnchorAddr(null);}}
                                anchorOrigin={{ vertical: 'top', horizontal: 'right', }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right', }}
                            >
                                <DaumAddressSearch open={Boolean(anchorAddr)} onSelect={onSelectAddress}/>
                            </Popover>
                    </Grid>



                    <Grid item xs={4}>
                        <PhoneField
                            dict={InputMap.officeTelno}
                            value={input.data.officeTelno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmTextField
                            dict={InputMap.storeStaffName}
                            value={input.data.storeStaffName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}></Grid>

                    <Grid item xs={4}>
                        <PhoneField
                            dict={InputMap.secuTelno}
                            value={input.data.secuTelno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmTextField
                            dict={InputMap.secuStaffName}
                            value={input.data.secuStaffName}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}></Grid>

                    <Grid item xs={4}>
                        <PhoneField
                            dict={InputMap.totTelno}
                            value={input.data.totTelno}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={8}></Grid>
                    <Grid item xs={12}>
                        <VzmCheckbox
                            dict={InputMap.ignoreDup}
                            value={input.data.ignoreDup}
                            onChange={onChangeValue}
                        />
                    </Grid>
                </Grid>
                
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    responseSaving.loading
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                    <Button
                        onClick={onClickSubmit}
                        disabled={input.hasError || !input.changed}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                    >저장</Button>
                    <Button onClick={onClickCancel}
                        color={AppStyle.mui.warning}
                        variant={AppStyle.mui.contained}
                    >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
